import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Link, 
  Divider, 
  Stack, 
  TextField, 
  Button, 
  IconButton,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SendIcon from '@mui/icons-material/Send';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const Footer: React.FC = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [email, setEmail] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      const errorMessage = t('footer.newsletter.error');
      setSnackbarMessage(typeof errorMessage === 'string' ? errorMessage : 'Please enter a valid email address');
      setSnackbarSeverity('error');
    } else {
      const successMessage = t('footer.newsletter.success');
      setSnackbarMessage(typeof successMessage === 'string' ? successMessage : 'Thank you for subscribing!');
      setSnackbarSeverity('success');
      setEmail('');
    }
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // Helper function to get string value from translation
  const getTranslationString = (key: string, defaultValue: string = ''): string => {
    const translation = t(key);
    return typeof translation === 'string' ? translation : defaultValue;
  };
  
  return (
    <Box
      sx={{
        bgcolor: '#fafafa',
        py: { xs: 4, md: 6 },
        position: 'relative',
        mt: 'auto',
        borderTop: '1px solid #eaeaea',
      }}
      component="footer"
    >
      {/* Back to top button */}
      <IconButton
        onClick={scrollToTop}
        sx={{
          position: 'absolute',
          top: -20,
          right: 20,
          bgcolor: 'primary.main',
          color: 'white',
          width: 40,
          height: 40,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          '&:hover': {
            bgcolor: 'primary.dark',
          },
          transition: 'all 0.2s ease',
          zIndex: 2
        }}
      >
        <KeyboardArrowUpIcon fontSize="small" />
      </IconButton>
      
      <Container maxWidth="lg">
        {/* Main Footer Content */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: '2fr repeat(3, 1fr)' 
          },
          gap: 4
        }}>
          {/* Company Info */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' } }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2,
                fontWeight: 600,
                fontFamily: '"Orbitron", "Rajdhani", sans-serif',
                color: 'primary.main',
                fontSize: '1rem',
                letterSpacing: '0.5px'
              }}
            >
              {getTranslationString('footer.title', 'Ares Code Works')}
            </Typography>
            
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 3, 
                maxWidth: 350, 
                fontSize: '0.8rem', 
                textAlign: { xs: 'center', md: 'left' },
                fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                lineHeight: 1.6
              }}
            >
              {getTranslationString('footer.subtitle')}
            </Typography>
            
            {/* Newsletter Subscription */}
            <Box sx={{ mb: 3, width: '100%', maxWidth: '320px' }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  mb: 1.5,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'primary.main',
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif'
                }}
              >
                {getTranslationString('footer.newsletter.title', 'Subscribe to Our Newsletter')}
              </Typography>
              <Box 
                component="form" 
                onSubmit={handleSubscribe}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  height: '44px',
                  position: 'relative',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <TextField
                  placeholder={getTranslationString('footer.newsletter.placeholder')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{
                    height: '100%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px 0 0 8px',
                      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                      fontSize: '0.85rem',
                      height: '100%',
                      backgroundColor: 'white',
                      '& fieldset': {
                        borderRight: 'none',
                        height: '100%',
                        borderColor: 'transparent',
                        transition: 'all 0.2s ease'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0,0,0,0.1)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.light',
                        borderWidth: '1px'
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                      fontSize: '0.85rem',
                      padding: '12px 16px',
                      height: '20px',
                      lineHeight: '20px'
                    }
                  }}
                  InputProps={{
                    style: { height: '100%' }
                  }}
                />
                <Button 
                  type="submit"
                  variant="contained" 
                  color="primary"
                  sx={{ 
                    borderRadius: '0 8px 8px 0',
                    minWidth: 'unset',
                    height: '100%',
                    fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    fontWeight: 500,
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                    margin: 0,
                    padding: '0 16px',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <SendIcon fontSize="small" sx={{ transform: 'rotate(-45deg)', fontSize: '1.1rem' }} />
                </Button>
              </Box>
            </Box>
            
            <Stack direction="row" spacing={1}>
              <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                <TwitterIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                <LinkedInIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                <GitHubIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                <FacebookIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                <InstagramIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
          
          {/* Quick Links */}
          <Box>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600, 
                mb: 2,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                color: 'primary.main',
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                paddingBottom: '8px',
                display: 'inline-block'
              }}
            >
              {getTranslationString('footer.quickLinks')}
            </Typography>
            <Stack spacing={1}>
              <Link 
                component={RouterLink} 
                to="/" 
                color="text.secondary" 
                underline="none"
                sx={{ 
                  fontSize: '0.8rem',
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  fontWeight: 400,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {getTranslationString('nav.home')}
              </Link>
              <Link 
                component={RouterLink} 
                to="/#services" 
                color="text.secondary" 
                underline="none"
                sx={{ 
                  fontSize: '0.8rem',
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  fontWeight: 400,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {getTranslationString('nav.services')}
              </Link>
              <Link 
                component={RouterLink} 
                to="/portfolio" 
                color="text.secondary" 
                underline="none"
                sx={{ 
                  fontSize: '0.8rem',
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  fontWeight: 400,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {getTranslationString('nav.portfolio')}
              </Link>
              <Link 
                component={RouterLink} 
                to="/about" 
                color="text.secondary" 
                underline="none"
                sx={{ 
                  fontSize: '0.8rem',
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  fontWeight: 400,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {getTranslationString('nav.about')}
              </Link>
            </Stack>
          </Box>

          {/* Services - Simplifié */}
          <Box>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600, 
                mb: 2,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                color: 'primary.main',
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                paddingBottom: '8px',
                display: 'inline-block'
              }}
            >
              {getTranslationString('home.services.title')}
            </Typography>
            <Stack spacing={1}>
              <Link 
                href="#" 
                color="text.secondary" 
                underline="none"
                sx={{ 
                  fontSize: '0.8rem',
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  fontWeight: 400,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {getTranslationString('home.services.technicalSupport.title')}
              </Link>
              <Link 
                href="#" 
                color="text.secondary" 
                underline="none"
                sx={{ 
                  fontSize: '0.8rem',
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  fontWeight: 400,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {getTranslationString('home.services.accountManagement.title')}
              </Link>
              <Link 
                href="#" 
                color="text.secondary" 
                underline="none"
                sx={{ 
                  fontSize: '0.8rem',
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  fontWeight: 400,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {getTranslationString('home.services.dataAnalytics.title')}
              </Link>
              <Link 
                href="#" 
                color="text.secondary" 
                underline="none"
                sx={{ 
                  fontSize: '0.8rem',
                  fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                  fontWeight: 400,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {getTranslationString('home.services.cloudIntegration.title')}
              </Link>
            </Stack>
          </Box>
          
          {/* Contact Info - Design simplifié et moderne */}
          <Box>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600, 
                mb: 3,
                fontSize: '0.8rem',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                color: 'primary.main',
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                paddingBottom: '8px',
                display: 'inline-block'
              }}
            >
              {getTranslationString('footer.contactUs')}
            </Typography>
            <Stack spacing={2.5}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <EmailIcon 
                  sx={{ 
                    color: 'primary.main', 
                    mr: 1.5, 
                    mt: 0.2,
                    fontSize: '1.1rem' 
                  }} 
                />
                <Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.9rem',
                      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                      fontWeight: 500,
                      color: '#444',
                      mb: 0.2
                    }}
                  >
                    {getTranslationString('footer.emailAddress', 'contact@arescodeworks.com')}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.75rem',
                      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                      color: 'text.secondary'
                    }}
                  >
                    {getTranslationString('footer.emailDescription', 'Pour toute demande d\'assistance')}
                  </Typography>
                </Box>
              </Box>
              

              

              
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <AccessTimeIcon 
                  sx={{ 
                    color: 'primary.main', 
                    mr: 1.5, 
                    mt: 0.2,
                    fontSize: '1.1rem' 
                  }} 
                />
                <Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.9rem',
                      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                      fontWeight: 500,
                      color: '#444',
                      mb: 0.2
                    }}
                  >
                    {getTranslationString('footer.officeHours', 'Heures d\'ouverture')}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.75rem',
                      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                      color: 'text.secondary',
                      display: 'block',
                      mb: 0.2
                    }}
                  >
                    {getTranslationString('footer.weekdayHours', 'Lun-Ven: 9h00 - 18h00')}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.75rem',
                      fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                      color: 'text.secondary',
                      display: 'block'
                    }}
                  >
                    {getTranslationString('footer.weekendHours', 'Weekend: 10h00 - 15h00')}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Box>
        
        {/* Copyright Section - Simplifié */}
        <Divider sx={{ my: 3, borderColor: 'rgba(0, 0, 0, 0.06)' }} />
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pt: 1
        }}>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 1, sm: 0 },
              fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
              fontSize: '0.7rem',
              opacity: 0.8
            }}
          >
            {(() => {
              const copyright = getTranslationString('footer.copyright', '© {year} Ares Code Works. All rights reserved.');
              return copyright.replace('{year}', new Date().getFullYear().toString());
            })()}
          </Typography>
          <Stack 
            direction="row" 
            spacing={3}
            divider={<Box component="span" sx={{ color: 'rgba(0, 0, 0, 0.2)' }}>•</Box>}
          >
            <Link 
              href="#" 
              color="text.secondary" 
              underline="none" 
              sx={{ 
                fontSize: '0.7rem',
                fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                transition: 'color 0.2s ease',
                '&:hover': { 
                  color: 'primary.main' 
                } 
              }}
            >
              {getTranslationString('footer.privacyPolicy')}
            </Link>
            <Link 
              href="#" 
              color="text.secondary" 
              underline="none" 
              sx={{ 
                fontSize: '0.7rem',
                fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                transition: 'color 0.2s ease',
                '&:hover': { 
                  color: 'primary.main' 
                } 
              }}
            >
              {getTranslationString('footer.terms')}
            </Link>
            <Link 
              href="#" 
              color="text.secondary" 
              underline="none" 
              sx={{ 
                fontSize: '0.7rem',
                fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                transition: 'color 0.2s ease',
                '&:hover': { 
                  color: 'primary.main' 
                } 
              }}
            >
              {getTranslationString('footer.careers')}
            </Link>
          </Stack>
        </Box>
      </Container>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          variant="filled"
          sx={{ 
            width: '100%',
            fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
            fontSize: '0.8rem',
            '& .MuiAlert-message': {
              fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
              fontSize: '0.8rem'
            }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Footer; 